'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import SvgMagnifyingGlass from '@common/icon/SvgMagnifyingGlass';
import SvgNotification from '@common/icon/SvgNotification';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { Suspense } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { HeaderLogo } from '@/app/_components/HeaderLogo';
import { HeaderSearchContainer, useSearchOverlayStore } from '@/app/_components/SearchBar';
import { shouldShowDesktopTrendKeyword } from '@/app/_components/TrendKeyword/constants';
import { TrendKeywordInline } from '@/app/_components/TrendKeyword/TrendKeywordInline';
import { HeaderProfileAvatar } from '@/app/(allowed)/my/_components/HeaderProfileAvatar';
import { HIDE_DESKTOP_HEADER_PATHNAMES } from '@/components/template/GlobalLayout/constants';
import { DONATION_ROUTES, ROUTES, SELECTOR, WEL_MARKET_ROUTES } from '@/constants';
import { SUB_DOMAIN } from '@/constants/domain';
import { shouldRewriteRootToDonation } from '@/constants/yearEndDonationHome';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { usePartnerChromeClassName } from '@/hooks/usePartnerChromeClassName';
import { useRenderedPathname } from '@/hooks/useRenderedPathname';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { scrollToPageTop } from '@/utils';
interface DesktopNavItem {
  label: string;
  href: string;
  isActive: (pathname: string) => boolean;
}
const isSectionPath = (pathname: string, sectionRoot: string) => pathname === sectionRoot || pathname.startsWith(`${sectionRoot}/`);
const DESKTOP_NAV_ITEMS: DesktopNavItem[] = [{
  label: '홈',
  href: ROUTES.HOME.pathname,
  isActive: pathname => pathname === ROUTES.HOME.pathname
}, {
  label: '고향사랑기부',
  href: DONATION_ROUTES.HOME.pathname,
  isActive: pathname => isSectionPath(pathname, DONATION_ROUTES.HOME.pathname)
}];

//* 연말 이벤트 한정(WEL-5478) — 메인홈이 고기부로 rewrite되는 동안 홈 탭 대신 기존 메인홈(/feed)을 연다
const YEAR_END_DESKTOP_NAV_ITEMS: DesktopNavItem[] = [...DESKTOP_NAV_ITEMS.filter(item => item.href !== ROUTES.HOME.pathname), {
  label: '정책찾기+',
  href: ROUTES.FEED.pathname,
  isActive: pathname => pathname === ROUTES.FEED.pathname
}, {
  label: '혜택',
  href: ROUTES.BENEFIT.pathname,
  isActive: pathname => isSectionPath(pathname, ROUTES.BENEFIT.pathname)
}];
export const DesktopHeader = () => {
  const $ = _c(50);
  const partnerChromeClassName = usePartnerChromeClassName();
  const isLogin = useAuthStore(_temp);
  const pathname = useRenderedPathname();
  const isHomePage = usePathname() === ROUTES.HOME.pathname;
  const subDomain = useDeviceInfoStore(_temp2);
  const router = useCustomRouter();
  const isOverlayOpen = useSearchOverlayStore(_temp3);
  const openOverlay = useSearchOverlayStore(_temp4);
  const closeOverlay = useSearchOverlayStore(_temp5);
  const isHiddenPage = HIDE_DESKTOP_HEADER_PATHNAMES.includes(pathname);
  const isLocalMarketPath = isSectionPath(pathname, WEL_MARKET_ROUTES.HOME.pathname);
  const shouldHideTossLocalMarketHeader = isLocalMarketPath && subDomain === SUB_DOMAIN.TOSS;
  if (isHiddenPage || shouldHideTossLocalMarketHeader) {
    return null;
  }
  const isSearchPage = pathname === ROUTES.SEARCH.pathname;
  const navItems = shouldRewriteRootToDonation({
    subDomain: subDomain ?? null,
    pathname: ROUTES.HOME.pathname,
    method: "GET"
  }) ? YEAR_END_DESKTOP_NAV_ITEMS : DESKTOP_NAV_ITEMS;
  let t0;
  if ($[0] !== pathname) {
    t0 = isSectionPath(pathname, DONATION_ROUTES.HOME.pathname) ? ROUTES.SEARCH.withSearchParams({
      searchParams: {
        category: "gifts"
      }
    }) : ROUTES.SEARCH.pathname;
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const searchHref = t0;
  let t1;
  if ($[2] !== closeOverlay || $[3] !== isOverlayOpen || $[4] !== isSearchPage || $[5] !== openOverlay || $[6] !== router || $[7] !== searchHref) {
    t1 = () => {
      if (!isSearchPage) {
        router.push(searchHref);
        return;
      }
      (isOverlayOpen ? closeOverlay : openOverlay)();
    };
    $[2] = closeOverlay;
    $[3] = isOverlayOpen;
    $[4] = isSearchPage;
    $[5] = openOverlay;
    $[6] = router;
    $[7] = searchHref;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const handleSearchClick = t1;
  let t2;
  if ($[9] !== isHomePage) {
    t2 = e => {
      if (!isHomePage) {
        return;
      }
      e.preventDefault();
      scrollToPageTop();
    };
    $[9] = isHomePage;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const handleHomeClick = t2;
  let t3;
  if ($[11] !== partnerChromeClassName) {
    t3 = cn(partnerChromeClassName, "sticky top-0 z-20 w-full border-b border-Gray-200 bg-Gray-white mobile:hidden tablet:hidden");
    $[11] = partnerChromeClassName;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <HeaderLogo />;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== handleHomeClick) {
    t5 = <CustomLink href={ROUTES.HOME.pathname} className="flex shrink-0 items-center" aria-label={"Wello \uD648"} onClick={handleHomeClick}>{t4}</CustomLink>;
    $[14] = handleHomeClick;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== handleHomeClick || $[17] !== navItems || $[18] !== pathname) {
    let t7;
    if ($[20] !== handleHomeClick || $[21] !== pathname) {
      t7 = tab => <NavLink key={tab.href} href={tab.href} isActive={tab.isActive(pathname)} onClick={tab.href === ROUTES.HOME.pathname ? handleHomeClick : undefined}>{tab.label}</NavLink>;
      $[20] = handleHomeClick;
      $[21] = pathname;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    t6 = navItems.map(t7);
    $[16] = handleHomeClick;
    $[17] = navItems;
    $[18] = pathname;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[23] !== t6) {
    t7 = <div className="flex items-center gap-4">{t6}</div>;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t5 || $[26] !== t7) {
    t8 = <div className="flex items-center gap-32">{t5}{t7}</div>;
    $[25] = t5;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== pathname) {
    t9 = shouldShowDesktopTrendKeyword(pathname) && <TrendKeywordInline />;
    $[28] = pathname;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SvgMagnifyingGlass className="size-24" />;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== handleSearchClick) {
    t11 = <button type="button" aria-label={"\uAC80\uC0C9"} onClick={handleSearchClick} className="flex size-40 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900">{t10}</button>;
    $[31] = handleSearchClick;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <CustomLink href={ROUTES.PUSH.pathname} aria-label={"\uC54C\uB9BC\uC73C\uB85C \uC774\uB3D9"} className="flex size-40 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900"><SvgNotification className="size-24" /></CustomLink>;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  let t14;
  if ($[34] !== isLogin) {
    t13 = <HeaderProfileAvatar showProfileAvatar={isLogin} />;
    t14 = isLogin === false && <Button asChild={true} data-no-donation-accent={true} dimensions="H40" roundness="capsule"><CustomLink href={ROUTES.LOGIN.pathname}>로그인</CustomLink></Button>;
    $[34] = isLogin;
    $[35] = t13;
    $[36] = t14;
  } else {
    t13 = $[35];
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t11 || $[38] !== t13 || $[39] !== t14 || $[40] !== t9) {
    t15 = <div className="flex items-center gap-12">{t9}{t11}{t12}{t13}{t14}</div>;
    $[37] = t11;
    $[38] = t13;
    $[39] = t14;
    $[40] = t9;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t15 || $[43] !== t8) {
    t16 = <nav className="mx-auto flex h-72 max-w-screen-xl items-center justify-between px-40">{t8}{t15}</nav>;
    $[42] = t15;
    $[43] = t8;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  let t18;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Suspense fallback={null}><HeaderSearchContainer /></Suspense>;
    t18 = <div id={SELECTOR.DESKTOP_HEADER_ATTACHMENT} className="absolute inset-x-0 top-full" />;
    $[45] = t17;
    $[46] = t18;
  } else {
    t17 = $[45];
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t16 || $[48] !== t3) {
    t19 = <header data-goods-home-header={true} className={t3}>{t16}{t17}{t18}</header>;
    $[47] = t16;
    $[48] = t3;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  return t19;
};
const NavLink = t0 => {
  const $ = _c(8);
  const {
    href,
    isActive,
    children,
    onClick
  } = t0;
  const t1 = isActive ? "page" : undefined;
  const t2 = isActive ? "text-Gray-900" : "text-Gray-600 hover:text-Gray-900";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex h-40 items-center px-12 font-600 transition-colors typo-body2", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== href || $[4] !== onClick || $[5] !== t1 || $[6] !== t3) {
    t4 = <CustomLink href={href} aria-current={t1} className={t3} onClick={onClick}>{children}</CustomLink>;
    $[2] = children;
    $[3] = href;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(state_0) {
  return state_0.subDomain;
}
function _temp3(state_1) {
  return state_1.isOverlayOpen;
}
function _temp4(state_2) {
  return state_2.openOverlay;
}
function _temp5(state_3) {
  return state_3.closeOverlay;
}